import {
    BackendV2Client,
    DatasetUploadResult,
    DEFAULT_POLL_INTERVAL_MS,
    DEFAULT_TIMEOUT_MS,
} from '~/backend/backend-v2-client';
import { MAX_CONCURRENT_UPLOADS } from '~/backend/network-retry';
import {
    CreateRegistrationTaskPayload,
    RegistrationClient,
} from '~/backend/registration-client';
import {
    CompositeCbctClient,
    CreateCompositeCbctTaskPayload,
} from '~/backend/composite-cbct-client';
import { BlobUploadType, RequestParameters } from '~/backend/backend-client';
import { mapWithConcurrency } from '~/utils/concurrency';
import { handleError } from '~/utils/errors';

export type DatasetUploadSummary = {
    datasetId: string;
    totalFiles: number;
    uploadedFiles: number;
};

/** A dataset upload the backend did not accept, already reported to the user. */
class DatasetUploadRefusedError extends Error {
    public constructor() {
        super('A dataset upload was not accepted.');
        this.name = 'DatasetUploadRefusedError';
    }
}

/**
 * Creates a new dataset entry on the backend and returns its ID.
 */
export async function createImageDataset(
    client: BackendV2Client,
    request: RequestParameters,
    taskUID: string,
    nExpectedFiles: number
): Promise<string | null> {
    try {
        const datasetResponse = await client.createImageDataset({
            ...request,
            nExpectedFiles: nExpectedFiles.toString(),
            taskUID,
        });

        if (!datasetResponse || !(datasetResponse as { id: string }).id) {
            handleError('Failed to create dataset on backend.');
            return null;
        }

        const datasetId = (datasetResponse as { id: string }).id;
        return datasetId;
    } catch (error) {
        handleError(error, 'Error creating dataset on backend.');
        return null;
    }
}

/**
 * Uploads all files to an existing dataset on the backend,
 * {@link MAX_CONCURRENT_UPLOADS} at a time, and returns whether the dataset
 * verifiably holds exactly those files; a dataset that does not is reported
 * to the user here.
 *
 * Every upload must have been accepted; once one is not, no further file is
 * sent. A retried upload whose first delivery had landed delivers its slice
 * twice; a backend that counts deliveries rather than distinct files would
 * then count it twice, which could mask a slice that never arrived and let a
 * task run on an incomplete volume. So when the backend reports its file
 * counter, the highest value reported must equal the number of files sent,
 * and without a counter no upload may have needed a retry.
 */
export async function uploadDatasetFiles(
    client: BackendV2Client,
    request: RequestParameters,
    datasetId: string,
    files: BlobUploadType[]
): Promise<boolean> {
    let results: DatasetUploadResult[];
    try {
        results = await mapWithConcurrency(
            files,
            MAX_CONCURRENT_UPLOADS,
            async (file, idx) => {
                const result = await client.uploadFileToDataset(
                    request,
                    { file: file.file, filename: file.filename },
                    datasetId,
                    idx
                );
                if (!result.uploaded) {
                    // Already reported by the client; stops the uploads.
                    throw new DatasetUploadRefusedError();
                }
                return result;
            }
        );
    } catch (error) {
        if (error instanceof DatasetUploadRefusedError) {
            return false;
        }
        throw error;
    }

    // Uploads overlap, so each response observed the backend's counter at
    // a different point; only the highest reflects the final state.
    const counts = results.flatMap((result) =>
        result.datasetFileCount === undefined ? [] : [result.datasetFileCount]
    );
    if (counts.length > 0) {
        const reportedCount = Math.max(...counts);
        if (reportedCount === files.length) {
            return true;
        }
        handleError(
            new Error(
                `Dataset holds ${reportedCount} files after uploading ` +
                    `${files.length}. Refusing to submit a task on a ` +
                    `dataset whose contents cannot be trusted.`
            )
        );
        return false;
    }

    const retried = results.filter((result) => result.retried).length;
    if (retried === 0) {
        return true;
    }
    handleError(
        new Error(
            `${retried} of ${files.length} dataset uploads needed a retry ` +
                `and the backend reported no file count to verify them. ` +
                `Refusing to submit a task on a dataset whose contents ` +
                `cannot be trusted.`
        )
    );
    return false;
}

/**
 * Creates a registration (adapt+) task on the backend.
 */
export async function createRegistrationTask(
    client: RegistrationClient,
    request: RequestParameters,
    payload: CreateRegistrationTaskPayload
): Promise<string | null> {
    try {
        const response = (await client.createRegistrationTask(
            request,
            payload
        )) as { id?: string };

        if (!response || !response.id) {
            handleError(
                'Failed to create registration task: missing id in response.'
            );
            return null;
        }

        return response.id;
    } catch (error) {
        handleError(error, 'Error creating registration task.');
        return null;
    }
}

/**
 * Creates a composite-CBCT task on the backend.
 */
export async function createCompositeCbctTask(
    client: CompositeCbctClient,
    request: RequestParameters,
    payload: CreateCompositeCbctTaskPayload
): Promise<string | null> {
    try {
        const response = (await client.createCompositeCbctTask(
            request,
            payload
        )) as { id?: string };

        if (!response || !response.id) {
            handleError(
                'Failed to create composite CBCT task: missing id in response.'
            );
            return null;
        }

        return response.id;
    } catch (error) {
        handleError(error, 'Error creating composite CBCT task.');
        return null;
    }
}

/**
 * Monitors a task until results are available or timeout occurs.
 * Generic across task families — the client's `taskPathSegment`
 * decides which endpoint is polled.
 */
export async function monitorCreatedTask(
    client: BackendV2Client,
    request: RequestParameters,
    taskId: string,
    pollIntervalMs = DEFAULT_POLL_INTERVAL_MS,
    timeoutMs = DEFAULT_TIMEOUT_MS
): Promise<{ resultIds: string[] } | null> {
    try {
        const results = await client.monitorTask(
            request,
            taskId,
            pollIntervalMs,
            timeoutMs
        );

        if (!results) {
            handleError('Task failed or timed out.');
            return null;
        }

        return results;
    } catch (error) {
        handleError(error, 'Error monitoring task.');
        return null;
    }
}

/**
 * Downloads result files for a completed task.
 */
export async function downloadTaskResults(
    client: BackendV2Client,
    request: RequestParameters,
    taskId: string,
    resultIds: string[]
): Promise<File[]> {
    const downloadedFiles: File[] = [];

    for (const resultId of resultIds) {
        try {
            const blob = await client.fetchTaskResult(
                request,
                taskId,
                resultId
            );
            if (blob) {
                downloadedFiles.push(new File([blob], `${resultId}.dcm`));
            }
        } catch (error) {
            handleError(error, `Error downloading result file ${resultId}`);
        }
    }

    return downloadedFiles;
}

/**
 * Deletes results for a given task.
 */
export async function deleteTaskResult(
    client: BackendV2Client,
    request: RequestParameters,
    taskId: string
): Promise<boolean> {
    try {
        const taskResultDeleted = await client.deleteTask(request, taskId);
        return taskResultDeleted;
    } catch (error) {
        handleError(error, `Error deleting results for task ${taskId}`);
        return false;
    }
}
